import type { User } from "@supabase/supabase-js";
import type { Request } from "express";

// Standard API response wrapper types
export interface ApiResponse<T = any> {
    data?: T;
    message?: string;
    timestamp: string;
}

export interface ApiErrorResponse {
    error: {
        code: string;
        message: string;
    };
    timestamp?: string;
}

export interface PaginationMeta {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
    pagination: PaginationMeta;
}

// Extend Express Request to include authenticated user
export interface AuthenticatedRequest extends Request {
    user: User;
    userId: string;
    jwt: string;
    quotaInfo?: QuotaCheckResult;
    userTier?: FeatureTier;
}

// Auth middleware types
export interface AuthError {
    code: "MISSING_TOKEN" | "INVALID_TOKEN" | "TOKEN_EXPIRED" | "AUTH_ERROR";
    message: string;
    details?: unknown;
}

export interface AuthResult {
    success: boolean;
    user?: User;
    error?: AuthError;
}

// Story mode database types
export interface Story {
    id: string;
    user_id: string;
    title?: string;
    topic: string;
    status: "active" | "finalized" | "abandoned";
    current_step: number;
    total_steps: number;
    created_at: string;
    updated_at: string;
    hasShare?: boolean;
}

export interface StoryStep {
    id: string;
    story_id: string;
    step_number: number;
    story_text: string;
    drawing_prompt: string;
    user_prompt: string;
    story_followup_text?: string;
    is_completed: boolean;
    created_at: string;
}

export interface DrawingSubmission {
    id: string;
    story_id: string;
    step_id: string;
    image_data: string; // Display version (high quality)
    image_data_compressed?: string; // Compressed version for OpenAI Vision
    ai_interpretation?: string;
    attempt_number: number;
    sketching_time_ms?: number; // Time spent actively sketching in milliseconds
    is_latest: boolean; // Whether this is the latest/current drawing for the step
    submitted_at: string;
}

export interface StoryShare {
    id: string;
    story_id: string;
    share_token: string;
    created_by: string;
    created_at: string;
    expires_at?: string;
    original_expires_at?: string;
}

// API request/response types
export interface CreateStoryRequest {
    topic: string;
}

export interface CreateStoryResponse {
    story: Story;
    firstStep: StoryStep;
}

export interface StoryProgressRequest {
    currentStep: number;
    submissionId: string;
}

export interface StoryProgressResponse {
    story: Story;
    nextStep?: StoryStep;
}

export interface SubmitDrawingRequest {
    storyId: string;
    stepNumber: number;
    imageData: string; // Display version (high quality)
    imageDataCompressed: string; // Compressed version for AI analysis
    sketchingTimeMs?: number; // Time spent actively sketching in milliseconds
}

export interface SubmitDrawingResponse {
    submission: DrawingSubmission;
    canProgress: boolean;
}

// Story step with optional drawing data
export interface StoryStepWithDrawing extends StoryStep {
    drawing?: {
        ai_interpretation: string;
        image_data: string;
    };
}

export interface GetStoryResponse {
    story: Story;
    steps: StoryStepWithDrawing[];
    timestamp: string;
}

export interface GetFinalizedStoryResponse {
    story: Story;
    steps: StoryStepWithDrawing[];
    timestamp: string;
}

// Next step response types
export interface NextStepProgressResponse {
    story: Story;
    nextStep: StoryStep;
    action: "progressed";
    timestamp: string;
}

export interface NextStepFinalizationResponse {
    story: Story;
    action: "finalized";
    message: string;
    timestamp: string;
}

export type NextStepResponse = NextStepProgressResponse | NextStepFinalizationResponse;

// Quota system types
export type QuotaAction = "story_create" | "drawing_analyze" | "topic_generate";
export type UsageAction =
    | QuotaAction
    | "title_generate"
    | "step_generate"
    | "followup_generate"
    | "encouragement_generate";

export interface QuotaCheckResult {
    allowed: boolean;
    quotaType: QuotaAction;
    currentUsage: number;
    limit: number;
    resetDate: string;
    message?: string;
}

export interface QuotaUsage {
    used: number;
    limit: number;
}

export interface QuotaStatus {
    stories: QuotaUsage;
    drawings: QuotaUsage;
    topics: QuotaUsage;
    resetDate: string;
    bypassQuota: boolean;
}

export type Theme = "light" | "dark" | "system";

// User profile types
export type ReadingLevel = "beginner" | "age-appropriate" | "advanced";
export type AgeRangeKey = "3-5" | "5-8" | "8-10" | "11-13" | "13+";

export interface AgeRange {
    label: string;
    value: AgeRangeKey;
}

// Age range configuration - shared across frontend and backend
export const AGE_RANGES: readonly AgeRange[] = [
    { label: "3-5 years", value: "3-5" },
    { label: "5-8 years", value: "5-8" },
    { label: "8-10 years", value: "8-10" },
    { label: "11-13 years", value: "11-13" },
    { label: "13+ years", value: "13+" },
] as const;

// Default age range key (fallback when no profile exists)
export const DEFAULT_AGE_RANGE_KEY: AgeRangeKey = "5-8";

export interface UserProfile {
    user_id: string;
    age_range_key?: AgeRangeKey;
    reading_level?: ReadingLevel;
    display_name?: string;
    created_at: string;
    updated_at: string;
}

export interface UserProfileUpdateRequest {
    age_range_key?: AgeRangeKey;
    reading_level?: ReadingLevel;
    display_name?: string;
}

// User tier types
export type FeatureTier = "free" | "plus" | "pro";
export type PaymentPlan = "free" | "subscription" | "lifetime" | "trial";
export type TierStatus = "active" | "canceled" | "revoked";

// Tier status constants
export const TIER_STATUS = {
    ACTIVE: "active",
    CANCELED: "canceled",
    REVOKED: "revoked",
} as const;
